import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import Form from 'react-bootstrap/Form';
import {
  Checkbox,
  FormControlLabel,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import { getconsentuserURL, updateConsent } from '../../../../constants/constants';
import { uploadFeedbackAttachment, apiHostUrl, addComment } from '../../../../constants/constants';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  checkImageSignatureAndSize, clickFileInput,
} from '../../basic-component/CommonUtils';
import TextLabel from '../../basic-component/TextLabel';
import { InputGroup } from 'react-bootstrap';

const CommentDetails = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { csrfToken } = useSelector((state) => state.csrf);
  const { homeLink } = useSelector((state) => state.role);

  const fetchNamesrl = apiHostUrl + homeLink + getconsentuserURL;
  const fetchUrl    = apiHostUrl + homeLink + uploadFeedbackAttachment;
  const updateUrl   = apiHostUrl + homeLink + updateConsent;
  const addcommentUrl = apiHostUrl + homeLink + addComment;

  const multiPartHeaders = {
    "Content-Type": "multipart/form-data",
    "X-CSRF-TOKEN": csrfToken,
  };

  // ── form state ──────────────────────────────────────────────────────────────
  const [addConsent,   setAddConsent]   = useState(false);
  const [consentUser,  setConsentUser]  = useState("");
  const [consentUsers, setConsentUsers] = useState([]);
  const [consentUserId, setConsentUserId] = useState("");
  const [commentans,   setComment]      = useState('');
  const [filename,     setFilename]     = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [spanComponent, setSpanComponent] = useState(<>No File Chosen</>);
  const [validationError, setValidationError] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timline, setTimeline] = useState([]);
  const fileInputRef = useRef(null);
  const MAX_COMMENT_LENGTH = 5000;

  // ── file list display ───────────────────────────────────────────────────────
  const handleRemoveFile = (e, fileNameToRemove) => {
    setSelectedFiles(prev => prev.filter(f => f.name !== fileNameToRemove));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    if (selectedFiles.length > 0) {
      setSpanComponent(selectedFiles.map(file => (
        <div key={file.name} className="multi-attachment-select">
          {file.name}
          <div
            onClick={(e) => { e.stopPropagation(); handleRemoveFile(e, file.name); }}
            className="remove-file-cross">&times;
          </div>
        </div>
      )));
    } else {
      setSpanComponent(<div className="multi-no-file-choosen">{t('general.label.no.file.chosen')}</div>);
    }
  }, [selectedFiles]);

  const handleFileNameChange = () => {
    if (selectedFiles.length > 0) {
      setFilename(selectedFiles.map(f => f.name).join(", "));
    } else {
      setFilename(t('general.label.no.file.chosen'));
    }
  };

  const handleFileChange = (event, labelSelector) => {
    const newFiles = Array.from(event?.target?.files || []);
    const merged = [
      ...selectedFiles,
      ...newFiles.filter(nf => !selectedFiles.some(ef => ef.name === nf.name)),
    ];
    setSelectedFiles(merged);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── consent user list ───────────────────────────────────────────────────────
  const fetchnames = () => {
    const departmentId = props.data?.feedbackDetails?.department?.id;
    if (!departmentId) { setConsentUsers([]); return; }
    axios
      .post(fetchNamesrl, { id: departmentId }, { withCredentials: true, headers: { 'X-Csrf-Token': csrfToken, 'Content-Type': 'application/json' } })
      .then(res => {
        const users = res.data?.responseDetails;
        setConsentUsers(res.data?.success && Array.isArray(users) ? users : []);
      })
      .catch(() => setConsentUsers([]));
  };

  // ── field handlers ──────────────────────────────────────────────────────────
  const handleChange = (event) => {
    const checked = event.target.checked;
    setAddConsent(checked);
    if (!checked) {
      setConsentUser("");
      setConsentUserId("");
    } else {
      fetchnames();
    }
  };

  const handleCommentChange = (event) => {
    const value = event.target.value;
    setComment(value.length <= MAX_COMMENT_LENGTH ? value : value.slice(0, MAX_COMMENT_LENGTH));
  };

  const handleUserChange = (event) => {
    setConsentUser(event.target.value);
    setConsentUserId(event.target.value);
  };

  // ── form state notification ─────────────────────────────────────────────────
  useEffect(() => {
    if (props.onFormStateChange) {
      props.onFormStateChange({
        isSubmitting,
        canSubmit: !!commentans && commentans.length <= MAX_COMMENT_LENGTH,
      });
    }
  }, [isSubmitting, commentans]);

  // ── reset ───────────────────────────────────────────────────────────────────
  const resetForm = () => {
    setComment('');
    setSelectedFiles([]);
    setSpanComponent(<div className="multi-no-file-choosen">{t('general.label.no.file.chosen')}</div>);
    setValidationError({});
    setFilename('');
    setAddConsent(false);
    setConsentUser("");
    setConsentUserId("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmitResponse = (responseData) => {
    if (responseData?.success) {
      setTimeline(responseData);
      resetForm();
      props.consentDetails(responseData);
    }
  };

  // ── submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = () => {
    if (isSubmitting) return;
    const validation = {};

    if (addConsent && (!consentUserId || consentUserId === "")) {
      validation.consentError = t('general.exception.errorMessage.comment.consentUser.required');
    }
    if (!commentans) {
      validation.commentError = t('general.label.comment');
    } else if (commentans.length > MAX_COMMENT_LENGTH) {
      validation.commentError = `Maximum ${MAX_COMMENT_LENGTH} characters allowed`;
    }
    if (selectedFiles && selectedFiles.length > 0) {
      const fileErrors = [];
      const regex = /^[a-zA-Z0-9-_.() ]+$/;
      let validated = false;
      selectedFiles.forEach(file => {
        if (!regex.test(file.name) || file.name.length === 0) {
          fileErrors.push(`Filename "${file.name}" contains invalid characters.`);
        }
        checkImageSignatureAndSize({ files: [file] }, fileErrors, null, null, validated ? null : selectedFiles);
        validated = true;
      });
      if (fileErrors.length > 0) validation.fileError = fileErrors;
    }

    setValidationError(validation);
    if (Object.keys(validation).length !== 0) return;

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("_csrf", csrfToken);
    if (selectedFiles && selectedFiles.length > 0) {
      selectedFiles.forEach((file, i) => {
        formData.append(`addFileList[${i}].multipartFile`, file);
        formData.append(`addFileList[${i}].fileName`, file.name);
      });
    }

    if (props.consentTaggeddata === true) {
      formData.append("id", props?.data?.feedbackDetails?.id);
      formData.append("comment.commentGiven", commentans);
      consentUserId ? formData.append("user.id", consentUserId) : formData.append("user.id", 0);
      axios
        .post(addcommentUrl, formData, { withCredentials: true, headers: multiPartHeaders })
        .then(data => handleSubmitResponse(data.data))
        .catch(err => console.log(err))
        .finally(() => setIsSubmitting(false));
    } else {
      formData.append("id", props.consentID);
      formData.append("feedback.id", props?.data?.feedbackDetails?.id);
      formData.append("commentGiven", commentans);
      if (!commentans) { setIsSubmitting(false); return; }
      axios
        .post(updateUrl, formData, { withCredentials: true, headers: multiPartHeaders })
        .then(data => handleSubmitResponse(data.data))
        .catch(err => console.log(err))
        .finally(() => setIsSubmitting(false));
    }
  };

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      {addConsent && (
        <div className='mb-3'>
          <Typography variant="subtitle1" className='consent-label'>
            {t('general.lable.consentUser.text')}:<span className="text-danger">*</span>
          </Typography>
          <Form.Select
            value={consentUser}
            onChange={handleUserChange}
            className={consentUser ? 'border-blue consent-users' : ''}
          >
            <option value="">{t('general.lable.user.select')}</option>
            {consentUsers.map(u => (
              <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
            ))}
          </Form.Select>
          {validationError.consentError && (
            <Typography variant="caption" color="error">{validationError.consentError}.</Typography>
          )}
        </div>
      )}

      <div className='mb-3'>
        <Typography variant="subtitle1">
          {t('general.lable.otherReason.text')}:<span className="text-danger">*</span>
        </Typography>
        <Form.Control
          as="textarea"
          rows={props.footerSubmit ? 2 : 3}
          placeholder={t('general.error.comment.required')}
          value={commentans}
          onChange={handleCommentChange}
        />
        <div style={{ fontSize: props.footerSubmit ? '10px' : '12px', textAlign: 'right', marginTop: props.footerSubmit ? 0 : undefined }}>
          {commentans.length} / {MAX_COMMENT_LENGTH}
        </div>
        {validationError.commentError && (
          <Typography variant="caption" color="error">{validationError.commentError}.</Typography>
        )}
      </div>

      {commentans.length >= MAX_COMMENT_LENGTH && (
        <Typography variant="caption" color="error">
          Maximum {MAX_COMMENT_LENGTH} characters allowed
        </Typography>
      )}

      <div className='mb-3'>
        {props.keyComponent !== 'Close' && (
          <Form.Group controlId="formFile" className={props.footerSubmit ? 'mb-1' : 'mb-3'}>
            <TextLabel title={t("general.button.uploadFile")} isMandatory={false} />
            {!props.footerSubmit && (
              <span className="ms-2 fs-8">
                (.jpg, .jpeg, .gif, .png, .pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .zip, .rar, .xml, .xps, .msg, .eml)
              </span>
            )}
            {!props.footerSubmit && <br />}
            {!props.footerSubmit && (
              <Form.Label className="fw-medium feedback-add-files">
                {t("general.error.chooseFile.size")}
              </Form.Label>
            )}
            <InputGroup className="custom-file-uploader">
              <InputGroup.Text
                id="basic-addon1"
                onClick={(e) => clickFileInput(e, ".feedback-comment-file-list")}
                className="text-section float-start start-0 w-100 bg-body-secondary p-0 z-0 position-relative rounded"
              >
                <div className="p-2 me-2 border-end bg-body-secondary">
                  {t("general.label.choose.file")}
                </div>
                <div className="file-name-label-span">{spanComponent}</div>
              </InputGroup.Text>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".jpg,.jpeg,.gif,.png,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.xml,.xps,.msg,.eml"
                className="feedback-comment-file-list d-none"
                onChange={(e) => { handleFileChange(e); handleFileNameChange(); }}
              />
            </InputGroup>
            {validationError.fileError && (
              <Typography variant="caption" color="error">{validationError.fileError}</Typography>
            )}
          </Form.Group>
        )}
      </div>

      {props.consentTaggeddata && (
        <div className={`mb-3 ${props.footerSubmit ? "comment-add-consent-row" : ""}`}>
          {props.footerSubmit ? (
            <div className="form-check comment-add-consent-check">
              <input
                type="checkbox"
                id="chat-add-consent-input"
                checked={addConsent}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="chat-add-consent-input">
                {t("general.checkbox.addConsent.text")}
              </label>
            </div>
          ) : (
            <FormControlLabel
              control={<Checkbox checked={addConsent} onChange={handleChange} />}
              label={t("general.checkbox.addConsent.text")}
            />
          )}
        </div>
      )}

      {!props.footerSubmit && (
        <div className='text-end'>
          <Button
            variant="contained"
            onClick={handleSubmit}
            className='text-capitalize'
            disabled={!commentans || commentans.length > MAX_COMMENT_LENGTH || isSubmitting}
          >
            {isSubmitting ? (
              <span className="d-inline-flex align-items-center gap-2">
                <CircularProgress size={16} color="inherit" />
                {t('general.button.submit.label')}
              </span>
            ) : t('general.button.submit.label')}
          </Button>
        </div>
      )}
    </div>
  );
});

CommentDetails.displayName = 'CommentDetails';
export default CommentDetails;
